import React from 'react';

interface SidebarProps {
  currentModule: string;
  onModuleChange: (module: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentModule, onModuleChange }) => {
  const menuItems = [
    { id: 'dashboard', icon: '🚨', label: 'Dashboard', description: 'Centro de Operações' },
    { id: 'historico', icon: '📜', label: 'Histórico', description: 'Histórico de Ataques' },
    { id: 'protection', icon: '🛡️', label: 'Protection Manager', description: 'Gestão de Proteção' },
    { id: 'monitoring', icon: '📊', label: 'Monitoring', description: 'Monitoramento' },
    { id: 'reports', icon: '📈', label: 'Reports', description: 'Relatórios' }
  ];

  return (
    <aside style={{
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      width: '240px',
      background: 'var(--color-bg-secondary)',
      borderRight: '1px solid var(--color-border)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000
    }}>
      {/* Logo & Brand */}
      <div style={{
        padding: 'var(--spacing-6) var(--spacing-4)',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--gradient-card)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-2)'
        }}>
          <span style={{ fontSize: 'var(--text-2xl)' }}>🛡️</span>
          <div>
            <h2 style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-text-primary)',
              marginBottom: '0.125rem'
            }}>
              SOFTDADOS
            </h2>
            <p style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              SOC Platform
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav style={{
        flex: 1,
        padding: 'var(--spacing-4)',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-2)'
        }}>
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => onModuleChange(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-3)',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: currentModule === item.id
                  ? 'var(--gradient-hero)'
                  : 'transparent',
                border: currentModule === item.id
                  ? '1px solid var(--color-primary)'
                  : '1px solid transparent',
                borderRadius: 'var(--radius-lg)',
                color: currentModule === item.id
                  ? 'white'
                  : 'var(--color-text-secondary)',
                cursor: 'pointer',
                transition: 'all var(--transition-base)',
                textAlign: 'left',
                boxShadow: currentModule === item.id
                  ? 'var(--shadow-glow)'
                  : 'none',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (currentModule !== item.id) {
                  e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                  e.currentTarget.style.borderColor = 'var(--color-border-light)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentModule !== item.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: 'var(--text-xl)' }}>{item.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  marginBottom: '0.125rem'
                }}>
                  {item.label}
                </div>
                <div style={{
                  fontSize: 'var(--text-xs)',
                  opacity: 0.7
                }}>
                  {item.description}
                </div>
              </div>
              {currentModule === item.id && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  background: 'white',
                  borderRadius: '0 2px 2px 0'
                }} />
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* System Status Footer */}
      <div style={{
        padding: 'var(--spacing-4)',
        borderTop: '1px solid var(--color-border)',
        background: 'var(--color-bg-card)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-bg-elevated)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-border)'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'var(--color-success)',
            boxShadow: '0 0 10px var(--color-success)',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }} />
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-success-light)',
              marginBottom: '0.125rem'
            }}>
              Sistema Operacional
            </div>
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-tertiary)'
            }}>
              Proteção Ativa
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div style={{
          marginTop: 'var(--spacing-3)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--spacing-2)'
        }}>
          <div style={{
            padding: 'var(--spacing-2)',
            background: 'var(--color-bg-elevated)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)'
          }}>
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-tertiary)',
              marginBottom: '0.125rem'
            }}>
              Uptime
            </div>
            <div style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-success-light)'
            }}>
              99.98%
            </div>
          </div>
          <div style={{
            padding: 'var(--spacing-2)',
            background: 'var(--color-bg-elevated)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)'
          }}>
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-tertiary)',
              marginBottom: '0.125rem'
            }}>
              Tenants
            </div>
            <div style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-primary-light)'
            }}>
              12
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};
